/**
 * Returns a new object containing ONLY the fields that changed
 * between `original` and `updated` (deep comparison).
 *
 * Features:
 * - Ignores unchanged fields completely
 * - Supports nested objects (i18n, price, etc.)
 * - Never includes `undefined` fields
 * - If a nested object has partial changes, returns only changed nested fields
 * - Preserves original structure (no flattening)
 *
 * @example
 * const diff = getChangedFields(
 *   { name: 'Foo', price: { US: { price: 10 } } },
 *   { name: 'Bar', price: { US: { price: 10 }, EG: { price: 50 } } }
 * );
 * // → { name: 'Bar', price: { EG: { price: 50 } } }
 */
export function getChangedFields<T extends Record<string, any>>(
  original: T,
  updated: T,
): Partial<T> {
  const result: Partial<T> = {};

  const allKeys = new Set([
    ...Object.keys(original ?? {}),
    ...Object.keys(updated ?? {}),
  ]);

  for (const key of allKeys) {
    const origVal = original?.[key];
    const newVal = updated?.[key];

    // Skip undefined values in updated
    if (newVal === undefined) continue;

    // Both are plain objects — recurse
    if (isPlainObject(origVal) && isPlainObject(newVal)) {
      const nested = getChangedFields(origVal, newVal);
      if (Object.keys(nested).length > 0) {
        (result as any)[key] = nested;
      }
      continue;
    }

    // Primitive or array — do shallow compare
    if (!shallowEqual(origVal, newVal)) {
      (result as any)[key] = newVal;
    }
  }

  return result;
}

// ── Helpers ──────────────────────────────────────────────────────

function isPlainObject(val: any): val is Record<string, any> {
  return (
    val !== null &&
    typeof val === 'object' &&
    !Array.isArray(val) &&
    !(val instanceof Date)
  );
}

function shallowEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return false;
}
